import type { RefillItemInput, RefillKind, RefillSubmitInput } from '../../lib/api';
import { isEmail, isUsablePhone, type FieldErrors } from '../../lib/validate';

/*
 * Track ORX — the request step's rules, as pure functions returning message keys (like
 * `lib/validate.ts`). The server checks every one of them again; these catch a slip before the
 * round trip.
 */

/** At most this many prescriptions in one request (ORX-D9). */
export const MAX_ITEMS = 10;
/** The note's limit, in characters (ORX-D9). */
export const MAX_NOTE = 500;
/** A medication, a prescriber or a pharmacy's name: generous, and still a name. */
export const MAX_TEXT = 120;
/** An email address's limit once trimmed, as the server counts it (ORX-D20). */
export const MAX_EMAIL = 254;

export type RequestDraft = {
  kind: RefillKind | null;
  /** One box each, as typed. Blank boxes are ignored. */
  items: string[];
  prescriber: string;
  fromName: string;
  fromPhone: string;
  /** `YYYY-MM-DD` from the date box, or blank. */
  neededBy: string;
  note: string;
  /** ORX-D20 — optional, for this request only. Blank is none. */
  email: string;
};

export const EMPTY_REQUEST: RequestDraft = {
  kind: null,
  items: [''],
  prescriber: '',
  fromName: '',
  fromPhone: '',
  neededBy: '',
  note: '',
  email: '',
};

export type RequestField = 'kind' | 'items' | 'fromName' | 'fromPhone' | 'neededBy' | 'note' | 'email';

/** Today in the patient's own calendar, as the date box writes it. */
export function todayIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar date in `YYYY-MM-DD`. */
function isRealDate(value: string): boolean {
  const match = ISO_DATE.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/**
 * A prescription number when it is one — digits, perhaps with `#`, `Rx`, spaces or dashes, as a
 * label prints it — and otherwise the medication's name. The patient types either in the same box.
 */
export function itemFromText(text: string): RefillItemInput {
  const value = text.trim();
  const number = /^(?:rx\s*)?#?\s*(\d[\d\s-]*\d|\d)$/i.exec(value);
  if (number) return { rx: number[1]!.replace(/\s+/g, '') };
  return { name: value };
}

/** Every problem at once, so the patient fixes the form in one pass. */
export function validateRequest(draft: RequestDraft, today: string): FieldErrors<RequestField> {
  const errors: FieldErrors<RequestField> = {};
  if (!draft.kind) errors.kind = 'refill.kindRequired';
  // Asked once a kind is chosen: until then the list is not on screen.
  if (draft.kind && !draft.items.some((item) => item.trim())) errors.items = 'refill.itemsRequired';
  if (draft.kind === 'transfer') {
    if (!draft.fromName.trim()) errors.fromName = 'error.fieldRequired';
    if (draft.fromPhone.trim() && !isUsablePhone(draft.fromPhone)) errors.fromPhone = 'error.phoneInvalid';
  }
  const neededBy = draft.neededBy.trim();
  if (neededBy) {
    if (!isRealDate(neededBy)) errors.neededBy = 'refill.neededByInvalid';
    // Two `YYYY-MM-DD` strings compare as their dates do.
    else if (neededBy < today) errors.neededBy = 'refill.neededByPast';
  }
  if (draft.note.length > MAX_NOTE) errors.note = 'refill.noteTooLong';
  // Only an obviously wrong address: the server judges the rest (`email_invalid`).
  const email = draft.email.trim();
  if (email && (email.length > MAX_EMAIL || !isEmail(email))) errors.email = 'refill.emailInvalid';
  return errors;
}

/**
 * The submit's body: only what this kind of request uses, nothing blank, and each field only when
 * it has a value. The server whitelists its fields, so a stray one would be refused outright.
 */
export function buildSubmit(
  draft: RequestDraft & { kind: RefillKind },
  submitToken: string,
  textWhenReady: boolean,
): RefillSubmitInput {
  const items = draft.items
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, MAX_ITEMS)
    .map(itemFromText);
  const prescriber = draft.prescriber.trim();
  const fromName = draft.fromName.trim();
  const fromPhone = draft.fromPhone.trim();
  const note = draft.note.trim();
  const email = draft.email.trim();
  return {
    submitToken,
    kind: draft.kind,
    items,
    ...(draft.kind === 'renewal' && prescriber ? { prescriber } : {}),
    ...(draft.kind === 'transfer'
      ? { transferFrom: { name: fromName, ...(fromPhone ? { phone: fromPhone } : {}) } }
      : {}),
    ...(draft.neededBy.trim() ? { neededBy: draft.neededBy.trim() } : {}),
    ...(note ? { note } : {}),
    textWhenReady,
    ...(email ? { email } : {}),
  };
}

/**
 * The number the code went to, with its last four digits hidden — "(780) 555-••••" — so the screen
 * says enough to catch a typo and not the whole number to someone looking over a shoulder.
 */
export function maskedPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  // Four dots for the four hidden digits, not "…": the sentence's own full stop follows.
  if (local.length === 10) return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-••••`;
  return local.length > 4 ? `${local.slice(0, local.length - 4)}••••` : '••••';
}

/**
 * The number as typed, written the page's way — "(403) 555-0123" — for the patient to check before
 * sending when no code proved it (ORX-D19). Anything that is not ten digits is shown as typed.
 */
export function shownPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length === 10) return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  return phone.trim();
}

/** A `tel:` address from a number as people write it. */
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}
